import React, { useCallback, useState } from "react";
import { Flex, Grid } from "@chakra-ui/react";
import PathPickSearchProductsProductListItem from "@/features/pathpick/features/search-products/components/product-list/PathPickSearchProductsProductListItem";
import PathPickSearchProductsProductListItemCard from "@/features/pathpick/features/search-products/components/product-list/PathPickSearchProductsProductListItemCard";
import PathPickSizeStockModal from "@/features/pathpick/components/product/PathPickSizeStockModal";
import PathPickOrderQuantityModal from "@/features/pathpick/components/order/PathPickOrderQuantityModal";
import PathPickRequestProductsModal from "@/features/pathpick/components/request/PathPickRequestProductsModal";
import { getPathPickProductCardGrid } from "@/features/pathpick/components/product/PathPickProductCard";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import { PathPickProductListViewType } from "@/features/pathpick/features/search-products/types";
import {
  PathPickMarketPriceBasis,
  PathPickMutationProduct,
  PathPickProduct,
  PathPickProductKey,
} from "@/features/pathpick/types";
import { toPathPickProductKey } from "@/features/pathpick/utils";

interface Props {
  viewType: PathPickProductListViewType;
  items: PathPickProduct[];
  // 선택 상태 — 키는 "{type}:{id}", 값은 담기 body 항목({id, type}).
  selectedProducts: Map<PathPickProductKey, PathPickMutationProduct>;
  // 카드(memo)로 그대로 넘기므로 부모는 identity가 고정된 핸들러를 넘겨야 한다.
  onToggleItem: (item: PathPickProduct) => void;
  onPickItem: (item: PathPickProduct) => void;
  marketPriceBasis: PathPickMarketPriceBasis;
}

function PathPickSearchProductsProductListContent({
  viewType,
  items,
  selectedProducts,
  onToggleItem,
  onPickItem,
  marketPriceBasis,
}: Props) {
  // 카드 보기 설정(비교 표시/배치)에 따라 카드 폭과 열 수가 달라진다.
  const { layout } = usePathPickProductCardView();
  const [sizeStockItem, setSizeStockItem] = useState<PathPickProduct | null>(
    null
  );
  // 주문 수량 모달은 담기 여부와 무관하게 클릭한 상품 1건만 담아 연다.
  const [orderItems, setOrderItems] = useState<PathPickProduct[] | null>(null);
  // "확인 필요" 배지로 여는 요청 상세 — 카드마다 모달을 만들지 않고 목록에서 한 번만 렌더한다.
  // sku는 모달 검색창의 초기값으로 넘겨 그 상품만 걸러진 상태로 보여준다.
  const [requestDetailTarget, setRequestDetailTarget] = useState<{
    requestId: number;
    sku: string;
  } | null>(null);
  // 카드(memo)에 넘기는 핸들러는 identity가 고정돼야 한다.
  const handleOrderItem = useCallback(
    (item: PathPickProduct) => setOrderItems([item]),
    []
  );

  return (
    <>
      {viewType === "card" ? (
        <Grid {...getPathPickProductCardGrid(layout)}>
          {items.map((item) => {
            const key = toPathPickProductKey(item.productType, item.id);
            return (
              <PathPickSearchProductsProductListItemCard
                key={key}
                item={item}
                isSelected={selectedProducts.has(key)}
                onToggleSelect={onToggleItem}
                onOpenSizeStock={setSizeStockItem}
                onPick={onPickItem}
                onOrder={handleOrderItem}
                marketPriceBasis={marketPriceBasis}
                onOpenRequestDetail={setRequestDetailTarget}
              />
            );
          })}
        </Grid>
      ) : (
        <Flex flexDirection={"column"} gap={"8px"}>
          {items.map((item) => {
            const key = toPathPickProductKey(item.productType, item.id);
            return (
              <PathPickSearchProductsProductListItem
                key={key}
                item={item}
                isSelected={selectedProducts.has(key)}
                onToggleSelect={() => onToggleItem(item)}
                onOpenSizeStock={() => setSizeStockItem(item)}
                onPick={() => onPickItem(item)}
              />
            );
          })}
        </Flex>
      )}
      {sizeStockItem && (
        <PathPickSizeStockModal
          isOpen
          onClose={() => setSizeStockItem(null)}
          item={sizeStockItem}
        />
      )}
      {orderItems && (
        <PathPickOrderQuantityModal
          isOpen
          onClose={() => setOrderItems(null)}
          items={orderItems}
        />
      )}
      {requestDetailTarget && (
        <PathPickRequestProductsModal
          isOpen
          onClose={() => setRequestDetailTarget(null)}
          requestId={requestDetailTarget.requestId}
          initialQuery={requestDetailTarget.sku}
        />
      )}
    </>
  );
}

export default PathPickSearchProductsProductListContent;
